import {
  BarChartOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Button, Col, Modal, Row, Space, Tag, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Client } from "../../core/api-client";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import {
  API_REPORT_METADATA,
  API_REPORT_QUERY,
  API_SAVED_REPORTS,
  ROUTE_REPORT_BUILDER,
  ROUTE_REPORTS,
} from "../../shared/routes/private.routes";
import { exportCsv } from "../../shared/utils/export-csv";
import { formatDate, showNotification } from "../../shared/utils/utils";
import "./report-builder.less";

type ReportColumn = {
  key: string;
  label: string;
  type: string;
};

type ReportDataset = {
  key: string;
  label: string;
  columns: ReportColumn[];
};

const DATASET_LABELS: Record<string, string> = {
  staff: "Staff",
  project: "Project",
  milestone: "Milestone",
};

const DATE_FILTER_LABELS: Record<string, string> = {
  doj: "Date Of Joining",
  contractStartDate: "Contract Start Date",
  contractEndDate: "Contract End Date",
  lwd: "LWD",
  probationEndDate: "Probation End Date",
  resignationDate: "Resignation Date",
};

const ManageReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);
  const [totalRows, setTotalRows] = useState(0);
  const [datasets, setDatasets] = useState<ReportDataset[]>([]);
  const [exportingReportId, setExportingReportId] = useState<string>();

  const loadReports = useCallback(
    async (page = currentPage, nextPageSize = pageSize) => {
      setLoading(true);
      try {
        const response = await Client.get(API_SAVED_REPORTS, {
          params: { page, limit: nextPageSize },
        });
        setReports(response?.data?.data || []);
        setTotalRows(response?.data?.total || 0);
        setCurrentPage(response?.data?.page || page);
        setPageSize(response?.data?.limit || nextPageSize);
      } catch {
        showNotification("Failed to load saved reports", "error");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    loadReports(1, pageSize);
  }, []);

  const loadMetadata = useCallback(async () => {
    try {
      const response = await Client.get(API_REPORT_METADATA);
      setDatasets(response?.data?.datasets || []);
    } catch {
      showNotification("Failed to load report datasets", "error");
    }
  }, []);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const getExportColumns = (report: any) => {
    const dataset = datasets.find((item) => item.key === report.dataset);
    const columnMap = new Map(
      (dataset?.columns || []).map((column) => [column.key, column])
    );

    return (report.columns || []).map((columnKey: string) => {
      return (
        columnMap.get(columnKey) || {
          key: columnKey,
          label: columnKey,
          type: "string",
        }
      );
    });
  };

  const exportSavedReport = async (report: any) => {
    if (!report?.columns?.length) {
      showNotification("No columns selected for this report.", "warning");
      return;
    }

    setExportingReportId(report.reportId);
    try {
      const firstResponse = await Client.post(
        API_REPORT_QUERY,
        {
          dataset: report.dataset,
          columns: report.columns,
          dateFilter: report.dateFilter,
        },
        {
          params: {
            page: 1,
            limit: 1,
          },
        }
      );
      const total = firstResponse?.data?.total || 0;

      if (!total) {
        showNotification("No report data found to export.", "warning");
        return;
      }

      const response = await Client.post(
        API_REPORT_QUERY,
        {
          dataset: report.dataset,
          columns: report.columns,
          dateFilter: report.dateFilter,
        },
        {
          params: {
            page: 1,
            limit: total,
          },
        }
      );
      const exportRows = response?.data?.data || [];

      exportCsv(
        `${report.reportName || report.dataset || "saved"}_report.csv`,
        exportRows,
        getExportColumns(report)
      );
    } catch {
      showNotification("Failed to export report data", "error");
    } finally {
      setExportingReportId(undefined);
    }
  };

  const deleteReport = (report: any) => {
    Modal.confirm({
      title: "Delete report?",
      content: report?.reportName,
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        try {
          await Client.delete(`${API_SAVED_REPORTS}/${report.reportId}`);
          showNotification("Report deleted successfully", "success");
          loadReports(1, pageSize);
        } catch {
          showNotification("Failed to delete report", "error");
        }
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        title: "Report Name",
        dataIndex: "reportName",
        key: "reportName",
      },
      {
        title: "Dataset",
        dataIndex: "dataset",
        key: "dataset",
        render: (value: string) => DATASET_LABELS[value] || value || "-",
      },
      {
        title: "Columns",
        dataIndex: "columns",
        key: "columns",
        render: (value: string[]) => (
          <Tag color="blue">{Array.isArray(value) ? value.length : 0}</Tag>
        ),
      },
      {
        title: "Filter Date By",
        dataIndex: "dateFilter",
        key: "dateFilterField",
        render: (value: any) =>
          value?.field ? DATE_FILTER_LABELS[value.field] || value.field : "-",
      },
      {
        title: "From Date",
        dataIndex: "dateFilter",
        key: "fromDate",
        render: (value: any) => value?.fromDate ? formatDate(value.fromDate) : "-",
      },
      {
        title: "To Date",
        dataIndex: "dateFilter",
        key: "toDate",
        render: (value: any) => value?.toDate ? formatDate(value.toDate) : "-",
      },
      {
        title: "Updated Date",
        dataIndex: "updatedDate",
        key: "updatedDate",
        render: (value: string) => value ? formatDate(value) : "-",
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 150,
        render: (_: any, record: any) => (
          <Space>
            <Tooltip title="Edit">
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => navigate(`${ROUTE_REPORTS}/edit/${record.reportId}`)}
              />
            </Tooltip>
            <Tooltip title="Export">
              <Button
                type="link"
                icon={<DownloadOutlined />}
                loading={exportingReportId === record.reportId}
                onClick={() => exportSavedReport(record)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteReport(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [datasets, exportingReportId, navigate, pageSize]
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <BarChartOutlined />
              </div>
              <h1 className="app-page-title">Manage Reports</h1>
            </div>
            <p className="app-page-subtitle">
              View saved report definitions and create new reports.
            </p>
          </Col>
          <Col>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={() => loadReports(1, pageSize)}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(ROUTE_REPORT_BUILDER)}
              >
                Create New Report
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="app-table-card">
        <DataTableComponent
          pagination
          loading={loading}
          dataSource={reports.map((report) => ({
            ...report,
            key: report.reportId,
          }))}
          columns={columns}
          defaultPage={1}
          currentPage={currentPage}
          totalRows={totalRows}
          pageSize={pageSize}
          onPageChange={(page: number) => loadReports(page, pageSize)}
          onPageSizeChange={(page: number, nextPageSize: number) =>
            loadReports(page, nextPageSize)
          }
        />
      </div>
    </div>
  );
};

export default ManageReportsPage;
